import { type FC } from 'react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import { Image } from '@chakra-ui/react';
import {
  Button,
  Typography,
  View,
  YStack,
} from '@shinetools/sunshine-universal';

import { ChallengeStateName } from '__generated__/GQL';
import RedFlag from 'assets/brand/red-flag.png';
import SunshineModal from 'components/_core/SunshineModal';
import { useVerifyChallenge } from 'components/StrongAuthenticationChallengeModal/hooks/useVerifyChallenge';

import { modalMinHeight } from '../utils/constant';

export interface ChallengeRequestFraudRiskProps {
  challengeId: string;
  operation: string;
  userId: string;
  onClose: () => void;
}

const ChallengeRequestFraudRisk: FC<ChallengeRequestFraudRiskProps> = ({
  challengeId,
  onClose,
  operation,
  userId,
}) => {
  const { push } = useHistory();

  if (!challengeId || !operation || !userId) {
    throw new Error('Missing challengeId, name, operation or userId');
  }

  const { decline } = useVerifyChallenge();

  const handleUserDecline = useCallback(async () => {
    try {
      await decline({
        challengeId,
        name: ChallengeStateName.ChallengeDeclined,
      });
      onClose();
    } catch (err) {
      onClose();
    }
  }, [challengeId, decline, onClose]);

  const handleUserDeclineNotInitiator = useCallback(async () => {
    try {
      await decline({
        challengeId,
        name: ChallengeStateName.ChallengeDeclinedNotInitiator,
      });
      push(`/help/urgent`);
    } catch (err) {
      push(`/help/urgent`);
    }
  }, [push, challengeId, decline]);

  return (
    <>
      <SunshineModal.Body minHeight={modalMinHeight}>
        <YStack alignItems="center" gap={24} paddingBottom="$space.24">
          <Image
            src={RedFlag}
            style={{
              marginBottom: 24,
              opacity: 1,
              width: '30%',
            }}
          />

          <Typography.Header>
            <FormattedMessage id="authentication.challenge.fraudRisk.title" />
          </Typography.Header>
          <Typography.Text>
            <FormattedMessage id="authentication.challenge.fraudRisk.description" />
          </Typography.Text>
        </YStack>
      </SunshineModal.Body>
      <SunshineModal.Footer flexDirection="row" gap={16}>
        <View>
          <Button onPress={handleUserDecline} variant="tertiary">
            <FormattedMessage id="authentication.close" />
          </Button>
        </View>
        <View marginLeft="50%">
          <Button onPress={handleUserDeclineNotInitiator}>
            <FormattedMessage id="authentication.challenge.declined.contactSupport" />
          </Button>
        </View>
      </SunshineModal.Footer>
    </>
  );
};

export default ChallengeRequestFraudRisk;
